import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { formatDateTime } from '../utils/dateFormatter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  ArrowLeft,
  Crown,
  Settings,
  Save,
  RotateCcw,
  LogOut,
  FileText,
  Sparkles,
  AlertCircle,
  CheckCircle,
  User,
  ChevronDown,
  History,
  Eye,
  Undo2
} from 'lucide-react';

// Using relative URLs
const API = '/api';

const SuperAdminPrompts = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [selectedFramework, setSelectedFramework] = useState('SOC 2');
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  
  // Version history state
  const [versionHistory, setVersionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [reverting, setReverting] = useState(false);

  const frameworks = ['SOC 2', 'GDPR', 'ISO 27001', 'PCI DSS', 'HIPAA'];

  useEffect(() => {
    fetchPromptTemplates();
  }, []);

  useEffect(() => {
    loadFrameworkPrompt();
  }, [selectedFramework, promptTemplates]);

  useEffect(() => {
    setHasChanges(currentPrompt !== originalPrompt);
  }, [currentPrompt, originalPrompt]);

  const fetchPromptTemplates = async () => {
    try {
      const response = await axios.get(`${API}/admin/prompt-templates`);
      setPromptTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch prompt templates');
      console.error('Prompt templates fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFrameworkPrompt = () => {
    const template = promptTemplates.find(t => t.framework === selectedFramework);
    if (template) {
      setCurrentPrompt(template.prompt_text);
      setOriginalPrompt(template.prompt_text);
    } else {
      setCurrentPrompt('');
      setOriginalPrompt('');
    }
  };

  const handleSavePrompt = async () => {
    if (!currentPrompt.trim()) {
      toast.error('Prompt cannot be empty');
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/prompt-templates/${selectedFramework}`, {
        prompt_text: currentPrompt
      });
      
      setOriginalPrompt(currentPrompt);
      toast.success(`${selectedFramework} prompt template updated successfully`);
      
      // Refresh the templates to get the new version
      await fetchPromptTemplates();
    } catch (error) {
      toast.error('Failed to update prompt template');
      console.error('Prompt update error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPrompt = () => {
    setCurrentPrompt(originalPrompt);
    toast.info('Changes reset to last saved version');
  };

  const fetchVersionHistory = async (framework) => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/admin/prompt-templates/${framework}/history`);
      setVersionHistory(response.data);
    } catch (error) {
      toast.error('Failed to fetch version history');
      console.error('Version history fetch error:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleShowHistory = () => {
    setShowHistory(true);
    fetchVersionHistory(selectedFramework);
  };

  const handlePreviewVersion = (version) => {
    setPreviewVersion(version);
    setShowPreviewDialog(true);
  };

  const handleRevertToVersion = async (version) => {
    setReverting(true);
    try {
      await axios.post(`${API}/admin/prompt-templates/${selectedFramework}/revert/${version.version}`);
      toast.success(`Reverted ${selectedFramework} to version ${version.version}`);
      setShowPreviewDialog(false);
      setShowHistory(false);
      // Refresh templates
      await fetchPromptTemplates();
    } catch (error) {
      toast.error('Failed to revert to version');
      console.error('Revert error:', error);
    } finally {
      setReverting(false);
    }
  };

  const handleUseVersionText = (version) => {
    setCurrentPrompt(version.prompt_text);
    setShowPreviewDialog(false);
    setShowHistory(false);
    toast.info(`Loaded version ${version.version} text into editor. Save to create a new version.`);
  };

  const getFrameworkColor = (framework) => {
    const colors = {
      'SOC 2': 'bg-blue-100 text-blue-700 border-blue-200',
      'GDPR': 'bg-red-100 text-red-700 border-red-200',
      'ISO 27001': 'bg-purple-100 text-purple-700 border-purple-200',
      'PCI DSS': 'bg-orange-100 text-orange-700 border-orange-200',
      'HIPAA': 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[framework] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getPromptStats = (prompt) => {
    const words = prompt.trim().split(/\s+/).length;
    const chars = prompt.length;
    const lines = prompt.split('\n').length;
    return { words, chars, lines };
  };

  const currentStats = getPromptStats(currentPrompt);

  if (loading) {
    return (
      <div className="min-h-screen bg-appbg">
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-appbg">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/admin')}
                className="flex items-center space-x-2"
                data-testid="back-to-admin"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Admin Dashboard</span>
              </Button>
              
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-navy-600 to-navy-700 rounded-xl flex items-center justify-center">
                  <Settings className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">All Prompts</h1>
                  <p className="text-sm text-slate-600">Configure AI prompts for control generation</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Badge className="bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-100 hover:!text-purple-700">
                <Crown className="w-3 h-3 mr-1" />
                Super Admin
              </Badge>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="outline" 
                    className="flex items-center gap-2 border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                  >
                    <User className="w-4 h-4 text-slate-600" />
                    <span className="text-sm font-medium text-slate-900 max-w-[150px] truncate">
                      {user?.full_name || user?.email?.split('@')[0]}
                    </span>
                    <ChevronDown className="w-4 h-4 text-slate-500" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium text-slate-900">{user?.email}</p>
                      <p className="text-xs text-slate-500">Super Administrator</p>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={logout}
                    className="text-red-600 focus:text-red-600 cursor-pointer"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Framework Selection */}
        <div className="mb-8 animate-fadeIn">
          <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-navy-600" />
                <span>Framework Selection</span>
              </CardTitle>
              <CardDescription>
                Select a compliance framework to edit its AI prompt template
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {frameworks.map((framework) => {
                  const template = promptTemplates.find(t => t.framework === framework);
                  const isSelected = selectedFramework === framework;
                  
                  return (
                    <Card 
                      key={framework}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                        isSelected 
                          ? 'ring-2 ring-navy-500 border-navy-500 bg-emerald-50'
                          : 'hover:border-slate-300'
                      }`}
                      onClick={() => setSelectedFramework(framework)}
                      data-testid={`framework-${framework}`}
                    >
                      <CardContent className="p-4 text-center">
                        <Badge 
                          variant="outline"
                          className={`${getFrameworkColor(framework)} mb-2`}
                        >
                          {framework}
                        </Badge>
                        <div className="space-y-1">
                          {template?.sequence_number && (
                            <p className="text-xs font-mono font-bold text-navy-600 bg-navy-50 rounded px-2 py-0.5 inline-block">
                              {template.sequence_number}
                            </p>
                          )}
                          <p className="text-sm font-medium text-slate-900">
                            Version {template?.version || 1}
                          </p>
                          <p className="text-xs text-slate-500">
                            {template ? 'Active' : 'Default'}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Prompt Editor */}
          <div className="lg:col-span-3 space-y-6 animate-slideIn">
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      <span>{selectedFramework} Prompt Template</span>
                    </CardTitle>
                    <CardDescription>
                      Edit the AI prompt that generates compliance controls for {selectedFramework}
                    </CardDescription>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {hasChanges && (
                      <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Unsaved
                      </Badge>
                    )}
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleShowHistory}
                      className="flex items-center space-x-1"
                      data-testid="version-history"
                    >
                      <History className="w-4 h-4" />
                      <span>Version History</span>
                    </Button>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetPrompt}
                      disabled={!hasChanges}
                      className="flex items-center space-x-1"
                      data-testid="reset-prompt"
                    >
                      <RotateCcw className="w-4 h-4" />
                      <span>Reset</span>
                    </Button>
                    
                    <Button
                      onClick={handleSavePrompt}
                      disabled={!hasChanges || saving}
                      className="bg-navy-600 hover:bg-navy-700 text-white flex items-center space-x-1"
                      data-testid="save-prompt"
                    >
                      {saving ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          <span>Save Changes</span>
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent>
                <Textarea
                  value={currentPrompt}
                  onChange={(e) => setCurrentPrompt(e.target.value)}
                  placeholder="Enter the AI prompt template for generating compliance controls..."
                  rows={25}
                  className="font-mono text-sm resize-none"
                  data-testid="prompt-textarea"
                />
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 animate-slideIn">
            {/* Prompt Statistics */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Prompt Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Words</span>
                  <span className="font-semibold text-slate-900">{currentStats.words}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Characters</span>
                  <span className="font-semibold text-slate-900">{currentStats.chars}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600">Lines</span>
                  <span className="font-semibold text-slate-900">{currentStats.lines}</span>
                </div>
                
                <div className="pt-3 border-t border-slate-200">
                  <div className="flex items-center space-x-2">
                    {hasChanges ? (
                      <>
                        <AlertCircle className="w-4 h-4 text-yellow-600" />
                        <span className="text-sm text-yellow-700">Unsaved changes</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-green-700">All changes saved</span>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Template Information */}
            <Card className="border-0 shadow-soft bg-white/80 backdrop-blur-sm">
              <CardHeader>
                <CardTitle className="text-lg">Template Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <span className="text-sm font-medium text-slate-700">Framework</span>
                  <div className="mt-1">
                    <Badge 
                      variant="outline"
                      className={getFrameworkColor(selectedFramework)}
                    >
                      {selectedFramework}
                    </Badge>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-slate-700">Current Version</span>
                  <p className="text-sm text-slate-900 mt-1">
                    {promptTemplates.find(t => t.framework === selectedFramework)?.version || 1}
                  </p>
                </div>
                
                <div>
                  <span className="text-sm font-medium text-slate-700">Last Updated</span>
                  <p className="text-sm text-slate-900 mt-1">
                    {promptTemplates.find(t => t.framework === selectedFramework)?.updated_at 
                      ? formatDateTime(promptTemplates.find(t => t.framework === selectedFramework).updated_at)
                      : 'Never'
                    }
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Usage Guidelines */}
            <Card className="border-0 shadow-soft bg-blue-50 border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg text-blue-800">Usage Guidelines</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-blue-700">
                  <p className="mb-2"><strong>Variables available:</strong></p>
                  <ul className="space-y-1 text-xs">
                    <li>• <code>{'{organization}'}</code> - Customer's organization name</li>
                  </ul>
                </div>
                
                <div className="text-sm text-blue-700">
                  <p className="mb-2"><strong>Best practices:</strong></p>
                  <ul className="space-y-1 text-xs">
                    <li>• Be specific about control requirements</li>
                    <li>• Include evidence examples</li>
                    <li>• Specify automated vs manual classification</li>
                    <li>• Request 15-20 controls for comprehensive coverage</li>
                  </ul>
                </div>
                
                <div className="bg-blue-100 border border-blue-200 rounded-lg p-3 mt-4">
                  <p className="text-xs text-blue-800">
                    <strong>Note:</strong> Changes only affect new audits. Existing audits keep their original controls.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Version History Dialog */}
      <Dialog open={showHistory} onOpenChange={setShowHistory}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <History className="w-5 h-5 text-navy-600" />
              <span>{selectedFramework} Version History</span>
            </DialogTitle>
            <DialogDescription>
              View and revert to previous versions of this prompt template
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {loadingHistory ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy-600"></div>
              </div>
            ) : versionHistory.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No version history available
              </div>
            ) : (
              <div className="space-y-3">
                {versionHistory.map((version, index) => {
                  const currentTemplate = promptTemplates.find(t => t.framework === selectedFramework);
                  const isCurrentVersion = currentTemplate && version.version === currentTemplate.version;
                  
                  return (
                    <Card 
                      key={version.version} 
                      className={`${isCurrentVersion ? 'border-navy-500 bg-navy-50' : 'hover:border-slate-300'}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isCurrentVersion ? 'bg-navy-600 text-white' : 'bg-slate-100 text-slate-600'
                            }`}>
                              <span className="font-bold text-sm">v{version.version}</span>
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <span className="font-medium text-slate-900">Version {version.version}</span>
                                {isCurrentVersion && (
                                  <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">
                                    Current
                                  </Badge>
                                )}
                                {version.sequence_number && (
                                  <Badge variant="outline" className="font-mono text-xs">
                                    {version.sequence_number}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">
                                {version.updated_at 
                                  ? formatDateTime(version.updated_at)
                                  : version.created_at 
                                    ? formatDateTime(version.created_at)
                                    : 'Unknown date'
                                }
                              </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePreviewVersion(version)}
                              className="flex items-center space-x-1"
                            >
                              <Eye className="w-4 h-4" />
                              <span>Preview</span>
                            </Button>
                            {!isCurrentVersion && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRevertToVersion(version)}
                                disabled={reverting}
                                className="flex items-center space-x-1 text-orange-600 border-orange-300 hover:bg-orange-50"
                              >
                                <Undo2 className="w-4 h-4" />
                                <span>Revert</span>
                              </Button>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-3 text-xs text-slate-500">
                          {version.prompt_text.substring(0, 150)}...
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHistory(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Version Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-blue-600" />
              <span>Version {previewVersion?.version} Preview</span>
              {previewVersion?.sequence_number && (
                <Badge variant="outline" className="font-mono ml-2">
                  {previewVersion.sequence_number}
                </Badge>
              )}
            </DialogTitle>
            <DialogDescription>
              {previewVersion?.updated_at 
                ? `Last updated: ${formatDateTime(previewVersion.updated_at)}`
                : previewVersion?.created_at
                  ? `Created: ${formatDateTime(previewVersion.created_at)}`
                  : ''
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
              <pre className="whitespace-pre-wrap text-sm font-mono text-slate-700">
                {previewVersion?.prompt_text}
              </pre>
            </div>
          </div>
          
          <DialogFooter className="flex justify-between">
            <div className="flex space-x-2">
              <Button 
                variant="outline" 
                onClick={() => handleUseVersionText(previewVersion)}
                className="flex items-center space-x-1"
              >
                <FileText className="w-4 h-4" />
                <span>Load into Editor</span>
              </Button>
              {promptTemplates.find(t => t.framework === selectedFramework)?.version !== previewVersion?.version && (
                <Button 
                  onClick={() => handleRevertToVersion(previewVersion)}
                  disabled={reverting}
                  className="flex items-center space-x-1 bg-orange-600 hover:bg-orange-700 text-white"
                >
                  {reverting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>Reverting...</span>
                    </>
                  ) : (
                    <>
                      <Undo2 className="w-4 h-4" />
                      <span>Revert to This Version</span>
                    </>
                  )}
                </Button>
              )}
            </div>
            <Button variant="outline" onClick={() => setShowPreviewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SuperAdminPrompts;